import React, { useState, useEffect, useMemo } from "react";
import { useLoading } from "../Loading";
import { and, collection, doc, getDoc, getDocs, onSnapshot, or, query, updateDoc, where } from "firebase/firestore";
import { useAuth } from "../../contexts/authContext";
import db from "../../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import MobileStepper from '@mui/material/MobileStepper';
import Button from '@mui/material/Button';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { CNav, CNavItem, CNavLink, CTabContent, CTabPane } from '@coreui/react'
import { cilXCircle, cilCheckCircle } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { DialogContent, DialogContentText } from "@mui/material";
import { Box } from "@mui/system";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { format } from "date-fns";
import { approveApplication, rejectApplication } from "../../utils/api";

function ApplicantList() {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const now = dayjs().utc().toDate();
    const { setLoading } = useLoading();
    const { currentUser } = useAuth();
    const [applicationData, setApplicationData] = useState();
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [openReasonDialog, setOpenReasonDialog] = useState(false);
    const [search, setSearch] = useState();
    const [selectedApplication, setSelectedApplication] = useState();
    const [activeKey, setActiveKey] = useState(1);
    const [petNames, setPetName] = useState();
    const [reason, setReason] = useState("");
    const [filters, setFilters] = useState(
        {
            petType: "all",
            appointmentStatus: "all",
            petName: "all",
        }
    );

    useEffect(() => {
        const intervalId = setInterval(() => {
            checkOverdueApplication(db);
        }, 30 * 60 * 100);
        return () => clearInterval(intervalId);
    }, [db]);


    useEffect(() => {
        if (!currentUser) {
            navigate('/');
            return;
        }
    
        const applicationRef = query(collection(db, 'Applications'), where('shelterId', '==', currentUser.uid));
    
        const unsubscribeStore = []; // Store all unsubscribe functions
    
        const unsubscribeShelterApplications = onSnapshot(applicationRef, (applicationSnap) => {
            if (applicationSnap.empty) {
                setApplicationData([]);
                setPetName([]);
                setLoading(false);
                return;
            }
    
            const applicationList = applicationSnap.docs.map((doc) => ({
                applicationId: doc.id,
                ...doc.data(),
            }));
    
            const petNameSet = new Set();
            const updatedDataMap = new Map(); // Map by applicationId
    
            applicationList.forEach((app) => {
                const { applicationId, petId } = app;
                const petRef = doc(db, "Pets", petId);
                const appointmentRef = doc(db, "Appointments", applicationId);
                const adopterRef = query(
                    collection(db, "Applications"),
                    and(
                        where("petId", "==", petId),
                        or(where("status", "==", "Pending"), where("status", "==", "Approved"))
                    )
                );
    
                const updateAndSetData = () => {
                    const finalList = Array.from(updatedDataMap.values());
                    setApplicationData(finalList);
                    setPetName(Array.from(petNameSet));
                };
    
                // Listen to pet doc
                const unsubscribePet = onSnapshot(petRef, (petSnap) => {
                    if (petSnap.exists()) {
                        const petData = petSnap.data();
                        const existing = updatedDataMap.get(applicationId) || { ...app };
                        petNameSet.add(petData.petName);
                        updatedDataMap.set(applicationId, {
                            ...existing,
                            petType: petData.petType,
                            petName: petData.petName,
                            petStatus: petData.status,
                        });
                        updateAndSetData();
                    }
                });
    
                // Listen to appointment doc
                const unsubscribeAppointment = onSnapshot(appointmentRef, (appointmentSnap) => {
                    const existing = updatedDataMap.get(applicationId) || { ...app };
                    let appointmentStatus = "Pending Scheduling";
                    if (appointmentSnap.exists()) {
                        appointmentStatus = appointmentSnap.data().status === "Cancel" ? "Pending Scheduling" : "Visit Scheduled";
                    }
                    updatedDataMap.set(applicationId, {
                        ...existing,
                        appointmentStatus,
                    });
                    updateAndSetData();
                });
    
                // Listen to count of applications per pet
                const unsubscribeAdopter = onSnapshot(adopterRef, (snapshot) => {
                    (async () => {
                        let applicantCount = 0;
                
                        for (const applicationDoc of snapshot.docs) {
                            const applicationId = applicationDoc.id;
                            const applicationData = applicationDoc.data();
                
                            if (applicationData.status === "Approved") {
                                try {
                                    const appointmentsRef = doc(db, "Appointments", applicationId);
                                    const appointmentsSnapshot = await getDoc(appointmentsRef);
                
                                    if (!appointmentsSnapshot.exists()) {
                                        applicantCount++;
                                    } else if (appointmentsSnapshot.data().status !== "Rejected") {
                                        applicantCount++;
                                    }
                                } catch (error) {
                                    console.error("Error fetching appointment:", error);
                                }
                            } else if (applicationData.status === "Pending") {
                                applicantCount++;
                            }
                        }
                
                        const existing = updatedDataMap.get(applicationId) || { ...app };
                        updatedDataMap.set(applicationId, {
                            ...existing,
                            count: applicantCount,
                        });
                        updateAndSetData();
                    })();
                });
    
                unsubscribeStore.push(unsubscribePet, unsubscribeAppointment, unsubscribeAdopter);
            });
    
            setLoading(false);
        }, (error) => {
            console.error("Error getting applications:", error);
            setLoading(false);
        });
    
        unsubscribeStore.push(unsubscribeShelterApplications);
    
        return () => {
            unsubscribeStore.forEach((unsub) => unsub());
        };
    }, [currentUser, navigate, setPetName]);
    


const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((pre) => ({
        ...pre,
        [name]: value,
    }));
};

const filteredApplication = useMemo(() => {
    if (!applicationData) {
        return [];
    }

    let filtered = applicationData.filter((application) => {
        const applicationName = application.email.toLowerCase();
        const searchTermLower = (search || "").toLowerCase();

        const typeMatch = filters.petType === "all" || application.petType === filters.petType;
        const appointmentMatch = filters.appointmentStatus === "all" || application.appointmentStatus === filters.appointmentStatus;
        const nameMatch = filters.petName === "all" || application.petName === filters.petName;
        const searchMatch = applicationName.includes(searchTermLower);
        const statusMatch =
            activeKey === 1
                ? application.status === "Pending"
                : activeKey === 2
                    ? application.status === "Approved"
                    : activeKey === 3
                        ? application.status === "Rejected"
                        : false;

        return typeMatch && searchMatch && statusMatch && appointmentMatch && nameMatch;
    });

    if (activeKey === 1 && filtered.length > 0) { // Pending
        filtered.sort((a, b) => a.timestamp.seconds - b.timestamp.seconds); // Nearest first
    } else if ((activeKey === 2 || activeKey === 3) && filtered.length > 0) { // Approved
        filtered.sort((a, b) => b.timestamp.seconds - a.timestamp.seconds); // Recently first
    }
    return filtered;
}, [applicationData, filters, search, activeKey]);



const handleReject = async (application) => {
    setOpenReasonDialog(false);
    setLoading(true);
    if (application) {
        try {
            const applicationRef = doc(db, "Applications", application.applicationId)
            await updateDoc(applicationRef, { status: "Rejected", reason: reason });

            try {
                    await rejectApplication(application.adopterId, application.petName, reason);

            } catch (error) {
                console.error("Error sending email:", error);
            }

            setApplicationData(preData => {
                return preData.map(doc => {
                    if (doc.applicationId === application.applicationId)
                        return { ...doc, status: "Rejected", reason: reason };
                    else
                        return doc;
                });
            });
            setLoading(false);

        }
        catch (error) {
            setLoading(false);
            console.error(error);
        }

    }
    setLoading(false)
    setReason("");

};

const handleApprove = async (application) => {

    setLoading(true);
    if (application) {
        try {
            const dueDate = dayjs().add(2, 'weeks').utc().toDate();
            const applicationRef = doc(db, "Applications", application.applicationId)
            await updateDoc(applicationRef, { status: "Approved", dueDate: dueDate });

            try {
                await approveApplication(application.adopterId, application.petName);

            } catch (error) {
                console.error("Error sending email:", error);
            }

            setApplicationData(preData => {
                return preData.map(doc => {
                    if (doc.applicationId === application.applicationId)
                        return { ...doc, status: "Approved", dueDate: dueDate };
                    else
                        return doc;
                });
            });
            setLoading(false);

        }
        catch (error) {
            setLoading(false);
            console.error(error);
        }

    }
    setLoading(false)

};

const checkOverdueApplication = async (db) => {
    const applicationRef = collection(db, "Applications");
    const q = query(applicationRef, where("status", "==", "Approved"), where("dueDate", "<", now));
    const snap = await getDocs(q);

    for (const app of snap.docs) {
        const applicationId = app.id;
        const appointmentRef = doc(db, "Appointments", applicationId);
        const appointmentSnap = await getDoc(appointmentRef);
        
        if ((!appointmentSnap.exists() || appointmentSnap.data().status === "Cancel") &&
        app.data().status !== "Rejected") {
            await updateDoc(app.ref, { status: "Rejected", dueDate: null, reason: "You have missed the deadline to book your appointment." });
            try {
                const petRef = doc(db, 'Pets', app.data().petId);
                const petSnap = await getDoc(petRef);
                if(petSnap.exists()){
                    await rejectApplication(app.data()?.adopterId, petSnap.data()?.petName, "You have missed the deadline to book your appointment.");
                } else {
                    console.error("pet document does not exist");
                }
            } catch (error) {
                console.error("Error sending email:", error);
            }
        }
        

    };

};

const handleClearFilters = () => {
    setFilters({
        petType: "all",
        appointmentStatus: "all",
        petName: "all",
    })
    setSearch("");
};

const handleSearchChange = (e) => {
    setSearch(e.target.value);

};


const viewApplication = (application) => {
    setSelectedApplication(application);
    setOpenDialog(true);
};

const deleteReason = (application) => {
    setSelectedApplication(application);
    setOpenReasonDialog(true);
};


const handleClose = () => {
    setOpenDialog(false);
    setOpenReasonDialog(false);
    setReason("");
}

const [activeStep, setActiveStep] = useState(0);
const applicationsPerPage = 10;
const maxSteps = Math.ceil(filteredApplication.length / applicationsPerPage);
const startIndex = activeStep * applicationsPerPage;
const currentApplication = filteredApplication.slice(startIndex, startIndex + applicationsPerPage);

const handleNext = () => {
    setActiveStep((prevActiveStep) => Math.min(prevActiveStep + 1, maxSteps - 1));
};

const handleBack = () => {
    setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
};

return (
    <div className="applicationListContainer">
        <h2>Application List</h2>
        <CNav variant="tabs" role="tablist">
            <CNavItem>
                <CNavLink active={activeKey === 1} onClick={() => setActiveKey(1)}>
                    Pending
                </CNavLink>
            </CNavItem>
            <CNavItem>
                <CNavLink active={activeKey === 2} onClick={() => setActiveKey(2)}>
                    Approved
                </CNavLink>
            </CNavItem>
            <CNavItem>
                <CNavLink active={activeKey === 3} onClick={() => setActiveKey(3)}>
                    Rejected
                </CNavLink>
            </CNavItem>
        </CNav>
        <div className="filterContainer">
            <input type="text" className="searchBox" placeholder="Search by applicant email" value={search} onChange={handleSearchChange} />
            <div>
                <label htmlFor="petType">Pet Types</label> <br></br>
                <select name="petType" id="petType" className="form-select form-select-sm"
                    onChange={handleFilterChange} value={filters.petType}
                >
                    <option value="all">All</option>
                    <option value="Dog">Dog</option>
                    <option value="Cat">Cat</option>
                    <option value="Other">Other</option>

                </select>
            </div>
            {petNames && <div>
                <label htmlFor="petName">Pet Name</label> <br></br>
                <select name="petName" id="petName" className="form-select form-select-sm"
                    onChange={handleFilterChange} value={filters.petName}
                >
                    <option value="all">All</option>
                    {petNames.map((name) =>

                        <option key={name} value={name}>{name}</option>
                    )

                    }

                </select>
            </div>}
            <div>
                <label htmlFor="appointmentStatus">Appointment Status</label> <br></br>
                <select name="appointmentStatus" id="appointmentStatus" className="form-select form-select-sm"
                    onChange={handleFilterChange} value={filters.appointmentStatus}
                >
                    <option value="all">All</option>
                    <option value="Pending Scheduling">Pending Scheduling</option>
                    <option value="Visit Scheduled">Visit Scheduled</option>

                </select>
            </div>
            <div className="clearBtnContainer">
                {(filters.petType !== "all" || search || filters.appointmentStatus !== "all" || filters.petName !== "all") && <button onClick={handleClearFilters} className="clearFilterBtn" >
                    Clear
                </button>}
            </div>
        </div>

        <CTabContent>
            <CTabPane role="tabpanel" aria-labelledby="home-tab" visible={true}>
                <table>
                    <thead >
                        <tr>
                            <th>Applicant Name</th>
                            <th>Contact</th>
                            <th>Pet Name</th>
                            <th>Pet Type</th>
                            <th>No. of Applicants</th>
                            <th>Pet Status</th>
                            <th>Submit At</th>
                            <th>Status</th>
                            {activeKey === 2 && <th>Appointment Status</th>}
                            <th>Details</th>
                            {activeKey === 1 && <th>Actions</th>}
                            {activeKey === 3 && <th>Reasons</th>}

                        </tr>
                    </thead>
                    <tbody>
                        {currentApplication.map((application) => (
                            <tr key={application.applicationId}>
                                <td>{application.name}</td>
                                <td>{application.phoneNumber}<br></br>{application.email}</td>
                                <td>{application.petName}</td>
                                <td>{application.petType}</td>
                                <td>{application.count}</td>
                                <td>{application.petStatus}</td>
                                <td>{format(application.timestamp.toDate(), 'M/d/yyyy, h:mm a')}</td>
                                <td>{application.status}</td>
                                {activeKey === 2 && <td>{application.appointmentStatus}</td>}
                                <td>
                                    <button
                                        className="viewApplication"
                                        onClick={() => viewApplication(application)}
                                    >
                                        View
                                    </button>
                                </td>
                                {
                                    application.status === "Pending" && <td>
                                        <div style={{ display: "flex", justifySelf: "right", gap: "35px", width: "100%" }}>
                                            <CIcon icon={cilCheckCircle} size="xxl"
                                                className="approve"
                                                onClick={() => handleApprove(application)}>

                                            </CIcon>
                                            <CIcon icon={cilXCircle} size="xxl"
                                                className="reject"
                                                onClick={() => deleteReason(application)}>

                                            </CIcon>
                                        </div>
                                    </td>
                                }
                                {activeKey === 3 && <td>{application.reason}</td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </CTabPane>
        </CTabContent>


        <Box sx={{ height: 60, maxWidth: 400, width: '100%', p: 2, margin: "auto" }}>
            <MobileStepper
                variant="text"
                steps={maxSteps}
                position="static"
                activeStep={activeStep}
                nextButton={
                    <Button size="small" onClick={handleNext} disabled={activeStep === maxSteps - 1 || maxSteps === 0}>
                        Next <KeyboardArrowRight />
                    </Button>
                }
                backButton={
                    <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                        <KeyboardArrowLeft /> Back
                    </Button>
                }
            />
        </Box>
        {selectedApplication && <Dialog style={{ zIndex: "99999" }}
            open={openDialog}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">
                {"Applicant Details"}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    <table>
                        <tbody>
                            <tr display="flex" gap={10}>
                                <td >Name:</td>
                                <td >{selectedApplication.name}</td>

                            </tr>
                            <tr display="flex" gap={10}>
                                <td >IC Number:</td>
                                <td >{selectedApplication.icNumber}</td>

                            </tr>
                            <tr display="flex" gap={12}>
                                <td >Age:</td>
                                <td >{selectedApplication.age}</td>
                            </tr>
                            <tr display="flex" gap={10}>
                                <td >Email:</td>
                                <td >{selectedApplication.email}</td>
                            </tr>
                            <tr display="flex" gap={9}>
                                <td >Gender:</td>
                                <td >{selectedApplication.gender}</td>
                            </tr>
                            <tr display="flex" gap={10}>
                                <td >Phone Number:</td>
                                <td >{selectedApplication.phoneNumber}</td>
                            </tr>
                            <tr display="flex" gap={10}>
                                <td >Address:</td>
                                <td >{selectedApplication.address}, {selectedApplication.postcode} {selectedApplication.city}, {selectedApplication.state}.</td>
                            </tr>
                            <tr display="flex" gap={10}>
                                <td >Who adopted for:</td>
                                <td >{selectedApplication.whoAdoptfor}</td>
                            </tr>
                            <tr display="flex" gap={10}>
                                <td >No. of Adults:</td>
                                <td >{selectedApplication.noOfAdults}</td>
                            </tr>
                            <tr display="flex" gap={10}>
                                <td >Had pet before:</td>
                                <td >{selectedApplication.hadPet}</td>
                            </tr>
                            {selectedApplication.hadPet === "Yes" &&
                                <tr display="flex" gap={10}>
                                    <td >No. of pets:</td>
                                    <td >{selectedApplication.noOfOtherPets}</td>
                                </tr>}
                            <tr display="flex" gap={10}>
                                <td >Had surrendered pet:</td>
                                <td >{selectedApplication.hadSurrendered}</td>
                            </tr>
                            {selectedApplication.hadSurrendered === "Yes" &&
                                <tr display="flex" gap={10}>
                                    <td >Surrendered Reason:</td>
                                    <td >{selectedApplication.surrenderedReason}</td>
                                </tr>
                            }

                            <tr display="flex" gap={10}>
                                <td >Type of Home:</td>
                                <td >{selectedApplication.typeOfHome}</td>
                            </tr>
                            <tr display="flex" gap={10}>
                                <td >Anybody Allergic:</td>
                                <td >{selectedApplication.petAllergic}</td>
                            </tr>
                            <tr display="flex" gap={10}>
                                <td >Main Caregiver:</td>
                                <td >{selectedApplication.mainCaregiver}</td>
                            </tr>
                            <tr display="flex" gap={10}>
                                <td >Caregiver Email:</td>
                                <td >{selectedApplication.emailofCaregiver}</td>
                            </tr>

                        </tbody>
                    </table>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>}

        {selectedApplication && <Dialog style={{ zIndex: "9999" }}
            open={openReasonDialog}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">
                {"Reason for rejection"}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} required
                        style={{ border: "1px solid #ccc", borderRadius: "5px", padding: "10px", width: "300px" }}></input>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={() => handleReject(selectedApplication)} disabled={!reason.trim()} autoFocus>
                    Save </Button>
            </DialogActions>
        </Dialog>}
    </div >
);
}

export default ApplicantList;